import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Receipt, AlertTriangle, Clock, RefreshCw, ChevronRight } from 'lucide-react';

/**
 * SuperAdmin panel: shows aggregate pending & overdue subscription payments
 * owed to Paydaai from all businesses.
 */
export const SubscriptionDebtsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(''); // '', 'pending', 'overdue'
  const API_URL = process.env.REACT_APP_API_URL;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/api/superadmin/subscription-debts${statusFilter ? `?status=${statusFilter}` : ''}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setData(data);
    } catch (_e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [API_URL, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt = (n) => (n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleDateString('tr-TR');
    } catch (_e) {
      return iso;
    }
  };

  if (!data) {
    return (
      <Card className="border border-[#E5E5E5]">
        <CardContent className="p-6 text-center text-sm text-[#595959]">
          {loading ? 'Yükleniyor...' : 'Veri alınamadı.'}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="subscription-debts-panel">
      {/* Totals */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="border border-amber-200 bg-amber-50/40" data-testid="debts-stat-pending">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="w-6 h-6 text-amber-600" />
            <div>
              <p className="text-xs text-[#595959]">Bekleyen</p>
              <p className="text-2xl font-bold text-amber-800">₺{fmt(data.total_pending)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-red-200 bg-red-50/40" data-testid="debts-stat-overdue">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            <div>
              <p className="text-xs text-[#595959]">Gecikmiş</p>
              <p className="text-2xl font-bold text-red-700">₺{fmt(data.total_overdue)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-[#E5E5E5] bg-white" data-testid="debts-stat-count">
          <CardContent className="p-4 flex items-center gap-3">
            <Receipt className="w-6 h-6 text-[#2563EB]" />
            <div>
              <p className="text-xs text-[#595959]">Toplam Açık Kayıt</p>
              <p className="text-2xl font-bold text-[#1A1A2E]">{data.count}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* List */}
      <Card className="border border-[#E5E5E5]">
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="text-lg text-[#2563EB]">Bekleyen Abonelik Borçları</CardTitle>
          <div className="flex items-center gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-md border border-[#E5E5E5] bg-white px-3 text-sm"
              data-testid="debts-filter-select"
            >
              <option value="">Tümü</option>
              <option value="pending">Bekleyen</option>
              <option value="overdue">Gecikmiş</option>
            </select>
            <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="debts-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!data.items || data.items.length === 0 ? (
            <p className="text-sm text-[#999] text-center py-6">Bekleyen borç yok ✓</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-[#595959] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-2 px-2">İşletme</th>
                    <th className="text-left py-2 px-2">Plan</th>
                    <th className="text-left py-2 px-2">Admin</th>
                    <th className="text-right py-2 px-2">Tutar</th>
                    <th className="text-left py-2 px-2">Vade</th>
                    <th className="text-left py-2 px-2">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.slice(0, 100).map((it) => (
                    <tr key={it.id} className="border-b border-[#F0F0F0]" data-testid={`debt-row-${it.id}`}>
                      <td className="py-2 px-2">
                        <div className="flex items-center gap-1.5 font-medium">
                          {it.business_name}
                          {it.is_trial && <Badge variant="secondary" className="text-[10px]">Demo</Badge>}
                          {it.is_frozen && <Badge variant="destructive" className="text-[10px]">Donduruldu</Badge>}
                        </div>
                      </td>
                      <td className="py-2 px-2 text-xs">{it.plan_label || it.plan || '-'}</td>
                      <td className="py-2 px-2 text-xs text-[#595959]">{it.admin_email}</td>
                      <td className="py-2 px-2 text-right font-mono font-medium">₺{fmt(it.amount)}</td>
                      <td className="py-2 px-2 text-xs">
                        {fmtDate(it.due_date)}
                        {it.status === 'overdue' && it.days_overdue > 0 && (
                          <span className="block text-[10px] text-red-600 font-medium">+{it.days_overdue} gün</span>
                        )}
                      </td>
                      <td className="py-2 px-2">
                        {it.status === 'overdue' ? (
                          <Badge className="bg-red-100 text-red-800 text-[11px]">Gecikmiş</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 text-[11px]">Bekliyor</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.items.length > 100 && (
                <p className="text-xs text-[#999] text-center mt-3">{data.items.length - 100} kayıt daha...</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
